import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { 
  HardDrive, 
  Download, 
  Upload, 
  AlertTriangle,
  Package,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  UserX,
  Trash2,
  Mail,
  Clock,
  Send,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBackup() {
  const [exportingFull, setExportingFull] = useState(false);
  const [importingFull, setImportingFull] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Clean account state
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [cleanPassword, setCleanPassword] = useState('');
  const [showCleanPassword, setShowCleanPassword] = useState(false);
  const [cleanAbsences, setCleanAbsences] = useState(true);
  const [cleanSchedule, setCleanSchedule] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [showCleanConfirm, setShowCleanConfirm] = useState(false);

  // Auto backup config state
  const [backupConfig, setBackupConfig] = useState({
    enabled: false,
    recipients: [],
    hour: 21,
    last_sent: null,
    last_status: null
  });
  const [recipientsInput, setRecipientsInput] = useState('');
  const [savingBackupConfig, setSavingBackupConfig] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchBackupConfig();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchBackupConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/backup-config`);
      setBackupConfig(res.data);
      setRecipientsInput((res.data.recipients || []).join(', '));
    } catch (err) {
      console.error('Error fetching backup config:', err);
    }
  };

  const handleSaveBackupConfig = async () => {
    setSavingBackupConfig(true);
    try {
      const recipients = recipientsInput
        .split(/[,;\n]+/)
        .map(e => e.trim())
        .filter(e => e);
      
      await axios.put(`${API}/admin/backup-config`, {
        enabled: backupConfig.enabled,
        recipients,
        hour: backupConfig.hour
      });
      
      toast.success('Configuration de sauvegarde enregistrée');
      fetchBackupConfig();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSavingBackupConfig(false);
    }
  };

  const handleSendTestBackup = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/admin/backup/send-test`);
      if (res.data.success) {
        toast.success(res.data.message);
        fetchBackupConfig();
      } else {
        toast.error(res.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors de l\'envoi du test'));
    } finally {
      setSendingTest(false);
    }
  };

  const handleFullExport = async () => {
    if (!password) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }
    
    setExportingFull(true);
    try {
      toast.info('Création de l\'archive complète...');
      const response = await axios.post(
        `${API}/admin/backup/full-zip`, 
        { password },
        { 
          responseType: 'blob',
          timeout: 120000
        }
      );
      const today = new Date().toISOString().split('T')[0];
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vitalis_complet_${today}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Archive complète téléchargée !');
      setShowPasswordModal(false);
      setPassword('');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Mot de passe incorrect');
      } else {
        toast.error(getErrorMessage(err, 'Erreur lors de l\'export'));
      }
    } finally {
      setExportingFull(false);
    }
  };

  const handleFullImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!window.confirm('⚠️ ATTENTION: Cette action va REMPLACER TOUTES les données existantes. Êtes-vous absolument sûr ?')) {
      e.target.value = '';
      return;
    }
    
    setImportingFull(true);
    try {
      toast.info('Import complet en cours...');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/import/full-zip`, formData, { timeout: 120000 });
      toast.success('Import complet réussi !');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur d\'import'));
    } finally {
      setImportingFull(false);
    }
    e.target.value = '';
  };

  const handleCleanAccount = async () => {
    if (!selectedUserId || !cleanPassword) {
      toast.error('Sélectionnez un utilisateur et entrez votre mot de passe');
      return;
    }
    
    if (!cleanAbsences && !cleanSchedule) {
      toast.error('Sélectionnez au moins une option à nettoyer');
      return;
    }
    
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/admin/clean-account`, {
        user_id: selectedUserId,
        password: cleanPassword,
        clean_absences: cleanAbsences,
        clean_schedule: cleanSchedule
      });
      
      const results = res.data.results.cleaned;
      const details = [];
      if (results.absences) details.push(`${results.absences} absences`);
      if (results.leave_allocations) details.push(`${results.leave_allocations} allocations congés`);
      if (results.schedule) details.push(`${results.schedule} créneaux`);
      if (results.time_corrections) details.push(`${results.time_corrections} corrections`);
      if (results.week_reports) details.push(`${results.week_reports} rapports`);
      
      toast.success(`${res.data.message} - Supprimé: ${details.join(', ') || 'aucune donnée'}`);
      setShowCleanConfirm(false);
      setSelectedUserId('');
      setCleanPassword('');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Mot de passe incorrect');
      } else {
        toast.error(getErrorMessage(err, 'Erreur lors du nettoyage'));
      }
    } finally {
      setCleaning(false);
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  // Generate hours options for selector
  const hoursOptions = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <HardDrive className="h-5 w-5" />
          Sauvegarde & Restauration
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Exportez et importez vos données pour les sauvegarder ou les migrer
        </p>
      </div>

      {/* SAUVEGARDE AUTOMATIQUE */}
      <div className="border-2 border-blue-500/50 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-950/20">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Sauvegarde Automatique par Email</h3>
              <p className="text-sm text-muted-foreground">
                Recevez une sauvegarde quotidienne par email à l'heure de votre choix.
              </p>
            </div>
          </div>

          <div className="space-y-4 bg-white/50 dark:bg-slate-800/50 rounded-lg p-4">
            {/* Toggle activation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label htmlFor="backup-enabled" className="text-sm font-medium">
                  Activer la sauvegarde automatique
                </Label>
              </div>
              <Switch
                id="backup-enabled"
                checked={backupConfig.enabled}
                onCheckedChange={(checked) => setBackupConfig(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            {/* Hour selector */}
            <div className="space-y-2">
              <Label htmlFor="backup-hour" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Heure d'envoi
              </Label>
              <select
                id="backup-hour"
                value={backupConfig.hour}
                onChange={(e) => setBackupConfig(prev => ({ ...prev, hour: parseInt(e.target.value) }))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                {hoursOptions.map(h => (
                  <option key={h} value={h}>
                    {h.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>

            {/* Recipients */}
            <div className="space-y-2">
              <Label htmlFor="backup-recipients" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Destinataires (un par ligne ou séparés par des virgules)
              </Label>
              <textarea
                id="backup-recipients"
                value={recipientsInput}
                onChange={(e) => setRecipientsInput(e.target.value)}
                placeholder="email1@example.com&#10;email2@example.com"
                className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                rows={3}
              />
            </div>

            {/* Last status */}
            {backupConfig.last_sent && (
              <div className="flex items-center gap-2 text-sm bg-muted/50 rounded p-2">
                {backupConfig.last_status === 'success' ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                <span>
                  Dernier envoi: {new Date(backupConfig.last_sent).toLocaleString('fr-CH')}
                  {backupConfig.last_status !== 'success' && (
                    <span className="text-red-600 ml-2">({backupConfig.last_status})</span>
                  )}
                </span>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={handleSaveBackupConfig} 
                disabled={savingBackupConfig}
                className="flex-1"
              >
                {savingBackupConfig ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement...</>
                ) : (
                  <>Enregistrer la configuration</>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSendTestBackup}
                disabled={sendingTest || !recipientsInput.trim()}
                className="flex-1"
              >
                {sendingTest ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />Envoyer un test</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* EXPORT/IMPORT COMPLET */}
      <div className="border-2 border-primary rounded-lg p-4 bg-primary/5">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/20 rounded-lg">
              <Package className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Migration Complète</h3>
              <p className="text-sm text-muted-foreground">
                Archive ZIP contenant tout : code source, données, configuration et credentials.
              </p>
            </div>
          </div>
          
          {showPasswordModal ? (
            <div className="bg-muted/50 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Lock className="h-4 w-4 text-primary" />
                Vérification de sécurité requise
              </div>
              <div className="space-y-2">
                <Label htmlFor="export-password">Entrez votre mot de passe pour confirmer</Label>
                <div className="relative">
                  <Input
                    id="export-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Votre mot de passe"
                    className="pr-10"
                    onKeyDown={(e) => e.key === 'Enter' && handleFullExport()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleFullExport} disabled={exportingFull || !password}>
                  {exportingFull ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Export...</> : <><Lock className="h-4 w-4 mr-2" />Confirmer</>}
                </Button>
                <Button variant="outline" onClick={() => { setShowPasswordModal(false); setPassword(''); }}>Annuler</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => setShowPasswordModal(true)} disabled={exportingFull || importingFull} className="flex-1">
                <Download className="h-4 w-4 mr-2" />Exporter ZIP
              </Button>
              <label className="flex-1">
                <Button size="lg" variant="outline" className="w-full" disabled={exportingFull || importingFull} asChild>
                  <span>{importingFull ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Import...</> : <><Upload className="h-4 w-4 mr-2" />Importer ZIP</>}</span>
                </Button>
                <input type="file" accept=".zip" className="hidden" onChange={handleFullImport} disabled={exportingFull || importingFull} />
              </label>
            </div>
          )}
        </div>
      </div>

      {/* CLEAN ACCOUNT */}
      <div className="border-2 border-destructive/50 rounded-lg p-4 bg-destructive/5">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-destructive/20 rounded-lg">
              <UserX className="h-6 w-6 text-destructive" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-destructive">Nettoyer un compte</h3>
              <p className="text-sm text-muted-foreground">
                Supprimer les congés et les données de planning (créneaux, corrections, heures supplémentaires, validations) d'un collaborateur (sans supprimer le compte).
              </p>
            </div>
          </div>

          {!showCleanConfirm ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="user-select">Sélectionner un collaborateur</Label>
                <select
                  id="user-select"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">-- Choisir un utilisateur --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email}) {!u.is_active ? '(désactivé)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cleanAbsences}
                    onChange={(e) => setCleanAbsences(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Congés & absences</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cleanSchedule}
                    onChange={(e) => setCleanSchedule(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Planning, corrections, heures supp. & validations</span>
                </label>
              </div>

              <Button
                variant="destructive"
                onClick={() => setShowCleanConfirm(true)}
                disabled={!selectedUserId || (!cleanAbsences && !cleanSchedule)}
                className="w-full sm:w-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Nettoyer le compte
              </Button>
            </div>
          ) : (
            <div className="bg-destructive/10 rounded-lg p-4 space-y-4 border border-destructive/30">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Confirmation requise</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Vous allez supprimer {cleanAbsences && cleanSchedule ? 'tous les congés et le planning' : cleanAbsences ? 'tous les congés' : 'tout le planning'} de <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong>.
                    Cette action est irréversible.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clean-password">Entrez votre mot de passe pour confirmer</Label>
                <div className="relative">
                  <Input
                    id="clean-password"
                    type={showCleanPassword ? 'text' : 'password'}
                    value={cleanPassword}
                    onChange={(e) => setCleanPassword(e.target.value)}
                    placeholder="Votre mot de passe"
                    className="pr-10"
                    onKeyDown={(e) => e.key === 'Enter' && handleCleanAccount()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCleanPassword(!showCleanPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCleanPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="destructive" onClick={handleCleanAccount} disabled={cleaning || !cleanPassword}>
                  {cleaning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Nettoyage...</> : <><Trash2 className="h-4 w-4 mr-2" />Confirmer la suppression</>}
                </Button>
                <Button variant="outline" onClick={() => { setShowCleanConfirm(false); setCleanPassword(''); }}>Annuler</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">Recommandations:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Activez la sauvegarde automatique pour une tranquillité d'esprit</li>
              <li>L'export manuel contient les credentials (mots de passe, SMTP)</li>
              <li>La sauvegarde automatique ne contient PAS les credentials sensibles</li>
              <li>Le nettoyage de compte est irréversible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
